import {
  EMPTY_BATTER_LEVEL,
  FULL_BATTERY_LEVEL,
  HIGH_BATTERY_LEVEL,
  LOW_BATTERY_LEVEL,
  NORMAL_BATTERY_BOTTOM_LEVEL,
  NORMAL_BATTERY_UP_LEVEL,
} from './ClickerStateStore.constants';
import {ClickerStateStoreState} from './ClickerStateStore.types';
import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {Store} from '@/stores/Store';
import {Config} from '@/types';
import {DateUtils} from '@/utils/DateUtils';
import {NumberUtils} from '@/utils/NumberUtils';

// улучшения в ImprovementsStore
export class ClickerStateStore extends Store<ClickerStateStoreState> {
  constructor(private readonly config: Config) {
    super({
      gems: '0',
      totalIncomePerHour: 0,
      energy: 0,
      maxEnergy: 0,
      energyRechargersCount: 0,
      energyMultiplier: 0,
      energyRecoveryInMinutes: 180,
    });
  }

  public isEnoughEnergyToSpend(energyToSpend: number): boolean {
    return this.state.energy >= energyToSpend;
  }

  // вернет количество очков, которое получено за потраченное количество энергии
  public getPointsByEnergy(energyToSpend: number): number {
    return energyToSpend * this.getMultiplier();
  }

  public setMultiplier(v: number): void {
    this.setValue('energyMultiplier', v);
  }

  // добавить очки
  public addPoints(v: number): void {
    const newValue = this.getParsedGems() + Math.abs(v);
    this.setValue('gems', String(newValue));
  }

  // уменьшить очки
  public decreasePoints(v: number): void {
    const newValue = this.getParsedGems() - Math.abs(v);
    this.setValue('gems', String(Math.max(0, newValue)));
  }

  public restoreEnergy(): void {
    this.setValue('energy', this.state.maxEnergy);
  }

  public addMultiplier(value: number): void {
    this.setValue('energyMultiplier', this.state.energyMultiplier * value);
  }

  public addTotalPerHourMine(value: number): void {
    this.setValue('totalIncomePerHour', this.state.totalIncomePerHour + value);
  }

  public addEnergy(value: number): void {
    const newValue = this.state.energy + value;
    this.setValue('energy', Math.min(newValue, this.state.maxEnergy));
  }

  // трата энергии означает увеличение очков
  public spendEnergy(energyToSpend: number): void {
    this.setValue('energy', Math.max(0, this.state.energy - energyToSpend));
    this.addPoints(this.getPointsByEnergy(energyToSpend));
  }

  public addEnergyChargeCount(value: number): void {
    this.setValue('energyRechargersCount', this.state.energyRechargersCount + value);
  }

  public decreaseEnergyChargeCount(value: number): void {
    this.setValue('energyRechargersCount', this.state.energyRechargersCount - value);
  }

  public isEmptyEnergy(): boolean {
    return Math.trunc(this.getState().energy) === 0;
  }

  public isFullEnergy(): boolean {
    const state = this.getState();
    return state.energy === state.maxEnergy;
  }

  // вернет текущее количество очков
  public getPoints(): number {
    return Math.floor(parseInt(this.getState().gems));
  }

  // вернет значение мультипликтора
  public getMultiplier(): number {
    return this.getState().energyMultiplier;
  }

  public getBatteryChargeLevelInPercent(): number {
    const state = this.getState();
    const percentLevel = NumberUtils.calcPercentage(state.energy, state.maxEnergy);
    return NumberUtils.clamp(EMPTY_BATTER_LEVEL, FULL_BATTERY_LEVEL, percentLevel);
  }

  public getEnergyChargeCount(): number {
    return this.getState().energyRechargersCount;
  }

  public getCoinsPerHourMine(): number {
    return this.getState().totalIncomePerHour;
  }

  public isEmptyCharges(): boolean {
    const state = this.getState();
    return state.energyRechargersCount === 0;
  }

  public applyCharge(): void {
    if (this.isEmptyCharges()) return;

    this.decreaseEnergyChargeCount(1);
    this.restoreEnergy();
  }

  // dev method
  public devSetEnergy(value: number): void {
    if (this.config.isProd) return;

    this.setValue('energy', value);
    this.setValue('maxEnergy', Math.max(value, this.state.maxEnergy));
  }

  public getTimeBeforeFullBatteryCharge(): number {
    const {maxEnergy, energy, energyRecoveryInMinutes} = this.state;

    const minutes = energyRecoveryInMinutes * (1 - energy / (maxEnergy || 1));

    return DateUtils.minutesToSeconds(minutes);
  }

  public static isHighBatteryLevel(percent: number): boolean {
    return percent >= HIGH_BATTERY_LEVEL;
  }

  public static isNormalBatteryLevel(percent: number): boolean {
    return NumberUtils.inRange(NORMAL_BATTERY_BOTTOM_LEVEL, NORMAL_BATTERY_UP_LEVEL, percent);
  }

  public static isLowBatteryLevel(percent: number): boolean {
    return percent <= LOW_BATTERY_LEVEL;
  }

  private getParsedGems(): number {
    return parseFloat(this.getState().gems);
  }
}

injected(ClickerStateStore, DI_TOKENS.config);
